import { useMemo, useState, useEffect } from "react";

export default function useShiftCardLogic({
  jobs,
  angeboteByJobId,
  viewMode,
  currentUser,
}) {
  const [expanded, setExpanded] = useState(false);

  /* =========================================================
     ✅ HELPERS
  ========================================================= */

  function countOpenSlots(type) {
    let count = 0;

    jobs.forEach((job) => {
      const list = angeboteByJobId[job.id] || [];

      list.forEach((a) => {
        if (a.type === type && !a.reservedBy) count++;
      });
    });

    return count;
  }

  function filterLeitung() {
    return jobs.filter((job) =>
      (angeboteByJobId[job.id] || []).some(
        (a) => a.type === "leitung" && !a.reservedBy,
      ),
    );
  }

  function filterStudent() {
    return jobs.filter((job) =>
      (angeboteByJobId[job.id] || []).some(
        (a) => a.type === "team" && !a.reservedBy,
      ),
    );
  }

  function filterMine() {
    return jobs.filter((job) =>
      (angeboteByJobId[job.id] || []).some(
        (a) => a.reservedBy?.id === currentUser.id,
      ),
    );
  }

  /* =========================================================
     ✅ FILTER JOBS
  ========================================================= */

  const filteredJobs = useMemo(() => {
    switch (viewMode) {
      case "leitung":
        return filterLeitung();
      case "student":
        return filterStudent();
      case "mine":
        return filterMine();
      default:
        return jobs;
    }
  }, [jobs, viewMode, angeboteByJobId, currentUser.id]);

  /* =========================================================
     ✅ COUNTS / FLAGS
  ========================================================= */

  const openLeitungCount = useMemo(
    () => countOpenSlots("leitung"),
    [jobs, angeboteByJobId],
  );

  const openStudentCount = useMemo(
    () => countOpenSlots("team"),
    [jobs, angeboteByJobId],
  );

  const hasOpenLeitung = openLeitungCount > 0;
  const hasOpenStudent = openStudentCount > 0;

  const hasMyReservation = filteredJobs.length > 0;

  /* =========================================================
     ✅ SORT JOBS
  ========================================================= */

  const sortedJobs = useMemo(() => {
    return [...filteredJobs].sort((a, b) => {
      const aIsLeitung = a.leitungSlots > 0;
      const bIsLeitung = b.leitungSlots > 0;

      if (aIsLeitung && !bIsLeitung) return -1;
      if (!aIsLeitung && bIsLeitung) return 1;
      return 0;
    });
  }, [filteredJobs]);

  /* =========================================================
     ✅ SLOT STATS
  ========================================================= */

  const { filledSlots, totalSlots } = useMemo(() => {
    let filled = 0;
    let total = 0;

    jobs.forEach((job) => {
      const list = angeboteByJobId[job.id] || [];

      total += list.length;
      filled += list.filter((a) => a.reservedBy).length;
    });

    return { filledSlots: filled, totalSlots: total };
  }, [jobs, angeboteByJobId]);

  /* =========================================================
     ✅ EXPAND LOGIC
  ========================================================= */

  useEffect(() => {
    if (viewMode === "mine") {
      setExpanded(filteredJobs.length > 0);
    } else if (viewMode === "leitung") {
      setExpanded(hasOpenLeitung);
    } else if (viewMode === "student") {
      setExpanded(hasOpenStudent);
    }
  }, [viewMode, filteredJobs, hasOpenLeitung, hasOpenStudent]);

  /* =========================================================
     ✅ SHOULD RENDER
  ========================================================= */

  const shouldRender = useMemo(() => {
    switch (viewMode) {
      case "mine":
        return hasMyReservation;
      case "leitung":
        return hasOpenLeitung;
      case "student":
        return hasOpenStudent;
      default:
        return true;
    }
  }, [viewMode, hasMyReservation, hasOpenLeitung, hasOpenStudent]);

  return {
    expanded,
    setExpanded,

    filteredJobs,
    sortedJobs,

    filledSlots,
    totalSlots,

    openLeitungCount,
    openStudentCount,

    hasOpenLeitung,
    hasOpenStudent,
    hasMyReservation,

    shouldRender,
  };
}
